// The answer review on the result page: what you answered, what was right and what it earned, for every format (v2.2).
// The review item is the set's item as GET /battles/{code}/review returns it; the newer formats' details (the number's
// answer, the steps and their order, every right option) are used when the server sends them and left out when it does not.
import type { Format, Graphic, HotspotSpec, MeAnswer, MultiSpec, NumberSpec, Option, OrderSpec } from '@shared/types';

export interface ReviewItem {
  index: number; stem: string; options: Option[]; correctId: string; explanation: string; format?: Format; graphic?: Graphic;
  number?: Partial<NumberSpec>; order?: Partial<OrderSpec>; multi?: Partial<MultiSpec>; hotspot?: Partial<HotspotSpec>;
}
export type Verdict = 'right' | 'partial' | 'wrong' | 'none';
export interface ReviewLines { verdict: Verdict; mine?: string; right?: string; points: string }

const textOf = (it: ReviewItem, id: string | undefined) => (id ? it.options.find((o) => o.id === id)?.text ?? it.order?.steps?.find((s) => s.id === id)?.text : undefined);
const list = (it: ReviewItem, ids: readonly string[] | undefined, sep: string) => {
  const texts = (ids ?? []).map((id) => textOf(it, id)).filter((t): t is string => Boolean(t));
  return texts.length ? texts.join(sep) : undefined;
};
const strings = (v: unknown): string[] | undefined => (Array.isArray(v) && v.every((x) => typeof x === 'string') ? v : undefined);
function numberText(v: unknown, spec: Partial<NumberSpec> | undefined): string | undefined {
  if (typeof v !== 'number' || !Number.isFinite(v)) return undefined;
  const d = typeof spec?.decimals === 'number' && spec.decimals >= 0 ? Math.min(4, spec.decimals) : 2;
  const n = v.toLocaleString('en-GB', { maximumFractionDigits: d });
  return spec?.unit ? `${n} ${spec.unit}` : n;
}

export function reviewLines(it: ReviewItem, a: MeAnswer | undefined): ReviewLines {
  const points = typeof a?.points === 'number' ? a.points : 0;
  const verdict: Verdict = !a ? 'none' : a.correct ? 'right' : points > 0 ? 'partial' : 'wrong';
  const pts = !a ? 'Not answered' : points > 0 ? `+${points.toLocaleString('en-GB')}` : 'No points';
  const allRight = strings(it.multi?.correctIds) ?? strings(it.hotspot?.correctIds) ?? [it.correctId];
  switch (it.format) {
    case 'number':
      return { verdict, points: pts, mine: numberText(a?.value, it.number), right: numberText(it.number?.answer, it.number) };
    case 'order':
      return { verdict, points: pts, mine: list(it, strings(a?.order), ' → '), right: list(it, strings(it.order?.correctOrder), ' → ') };
    case 'multi':
      if (it.multi?.mode !== 'rarity') return { verdict, points: pts, mine: list(it, strings(a?.optionIds), ', '), right: list(it, allRight, ', ') };
      return { verdict, points: pts, mine: textOf(it, a?.optionId), right: list(it, allRight, ', ') };
    case 'hotspot':
      return { verdict, points: pts, mine: textOf(it, a?.optionId), right: list(it, allRight, ', ') };
    default:
      return { verdict, points: pts, mine: textOf(it, a?.optionId), right: textOf(it, it.correctId) };
  }
}
